import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPieChart, FiLock, FiUsers, FiTrendingUp } = FiIcons;

const Tokenomics = () => {
  const tokenomicsData = [
    {
      label: 'Community',
      percentage: 60,
      color: 'from-pink-400 to-pink-600',
      description: 'Fair launch for the community'
    },
    {
      label: 'Liquidity',
      percentage: 25,
      color: 'from-purple-400 to-purple-600',
      description: 'Locked forever for stability'
    },
    {
      label: 'Marketing',
      percentage: 10,
      color: 'from-blue-400 to-blue-600',
      description: 'Growth and partnerships'
    },
    {
      label: 'Team',
      percentage: 5,
      color: 'from-green-400 to-green-600',
      description: 'Development and operations'
    }
  ];

  const stats = [
    {
      icon: FiPieChart,
      title: 'Total Supply',
      value: '1,000,000,000',
      suffix: 'PINKZILLA'
    },
    {
      icon: FiLock,
      title: 'Liquidity Locked',
      value: '100%',
      suffix: 'Forever'
    },
    {
      icon: FiUsers,
      title: 'Community Owned',
      value: '95%',
      suffix: 'Decentralized'
    },
    {
      icon: FiTrendingUp,
      title: 'Market Cap',
      value: '$2.5M',
      suffix: 'And Growing'
    }
  ];

  return (
    <section id="tokenomics" className="py-20 px-4 bg-gradient-to-br from-purple-50 to-pink-50">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">Tokenomics</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Built for the community, by the community. Our tokenomics ensure fair distribution and long-term sustainability.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 text-center shadow-lg"
            >
              <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={stat.icon} className="text-white text-xl" />
              </div>
              <div className="text-2xl font-bold text-gray-800 mb-1">{stat.value}</div>
              <div className="text-sm text-gray-600 mb-1">{stat.suffix}</div>
              <div className="text-xs text-gray-500">{stat.title}</div>
            </motion.div>
          ))}
        </div>

        {/* Tokenomics Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-8">Token Distribution</h3>
            {tokenomicsData.map((item, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-700">{item.label}</span>
                  <span className="font-bold text-gray-800">{item.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${item.percentage}%` }}
                    transition={{ duration: 1, delay: index * 0.2 }}
                    className={`h-3 rounded-full bg-gradient-to-r ${item.color}`}
                  />
                </div>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-3xl p-8 shadow-2xl"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Why Our Tokenomics Rock</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                  <span className="text-white text-xs">✓</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Fair Launch</h4>
                  <p className="text-sm text-gray-600">No pre-sale, no team allocation advantage</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                  <span className="text-white text-xs">✓</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Locked Liquidity</h4>
                  <p className="text-sm text-gray-600">25% locked forever for price stability</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                  <span className="text-white text-xs">✓</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Community First</h4>
                  <p className="text-sm text-gray-600">60% goes directly to the community</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                  <span className="text-white text-xs">✓</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Transparent</h4>
                  <p className="text-sm text-gray-600">All transactions visible on blockchain</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Tokenomics;